import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsiveWidth } from '../../hooks/useWindowSize';
import { cn } from '../../lib/utils';
import {
  Button,
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerContent,
  DrawerFooter,
  ConfirmationDialog
} from '../ui';
import { renderField } from './fieldRenderers';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import { QuestionCircleIcon, CheckIcon, ExclamationTriangleIcon, InfoIcon, XIcon } from '../ui/Icons';

function formatTrackingUserObject(user) {
  if (!user || typeof user !== 'object') return null;
  const name =
    (typeof user.userDisplayName === 'string' && user.userDisplayName.trim()) ||
    `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() ||
    (typeof user.userEmail === 'string' && user.userEmail.trim()) ||
    '';
  if (name) return name;
  if (user.id != null) return `User #${user.id}`;
  return null;
}

/** Evaluate field.dependsOn (simple value or function). */
function shouldShowFieldByDependsOn(field, formData, config) {
  if (!field?.dependsOn) return true;
  const { field: dependsOnField, value: dependsOnValue } = field.dependsOn;
  const dependsOnFieldValue = formData?.[dependsOnField];
  if (typeof dependsOnValue === 'function') {
    return !!dependsOnValue(formData, config?.lookupData || {});
  }
  return dependsOnFieldValue === dependsOnValue;
}

/**
 * Resolve user for tracking display.
 * - Uses nested objects on formData when present.
 * - If formData was normalized (lookup fields → ids), falls back to selectedRecord (original API row).
 * - Contract-style entities use `creator` / `updater` instead of nested createdBy/updatedBy.
 */
function resolveTrackingUserValue(formData, fieldConfig, selectedRecord) {
  const key = fieldConfig.userField;
  const raw = formData?.[key];
  if (raw != null && typeof raw === 'object') {
    return raw;
  }
  const fromSelected = selectedRecord?.[key];
  if (fromSelected != null && typeof fromSelected === 'object') {
    return fromSelected;
  }
  if (key === 'createdBy' && formData?.creator != null && typeof formData.creator === 'object') {
    return formData.creator;
  }
  if (key === 'updatedBy' && formData?.updater != null && typeof formData.updater === 'object') {
    return formData.updater;
  }
  if (key === 'creator' && formData?.creator != null && typeof formData.creator === 'object') {
    return formData.creator;
  }
  if (key === 'updater' && formData?.updater != null && typeof formData.updater === 'object') {
    return formData.updater;
  }
  // formData may hold only a numeric id (or diverge from the row) while selectedRecord still has the nested user
  if ((key === 'createdBy' || key === 'updatedBy') && selectedRecord) {
    const nested = selectedRecord[key];
    if (nested && typeof nested === 'object') {
      if (raw != null && typeof raw !== 'object') {
        const asNum = Number(raw);
        if (!Number.isNaN(asNum) && nested.id === asNum) {
          return nested;
        }
      }
      const idKey = key === 'createdBy' ? 'createdById' : 'updatedById';
      const idFromForm = formData?.[idKey];
      if (idFromForm != null && nested.id === Number(idFromForm)) {
        return nested;
      }
    }
  }
  return raw;
}

/**
 * FormDrawer - Add/Edit form drawer component
 * Extracted from GenericFormsPage for better maintainability
 */
const FormDrawer = ({
  // State
  isOpen,
  formMode,
  formData,
  formErrors,
  isLoading,
  isFormValid,
  formNotifications,
  entityName,
  selectedRecord,
  
  // Configuration
  config,
  fields,
  labels,
  
  // Callbacks
  onClose,
  onSubmit,
  setFormData,
  setFormErrors,
  setFilesToDelete,
  validateField,
  clearFormNotifications,
  setFormNotifications
}) => {
  const { theme } = useTheme();
  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const [isTaskDeleteConfirmOpen, setIsTaskDeleteConfirmOpen] = React.useState(false);
  const taskDeleteResolverRef = React.useRef(null);
  
  // Get responsive width based on screen size
  const responsiveWidth = useResponsiveWidth(config.ui?.drawerWidth || '800px');
  
  // Clear dependent fields when responsibleForActionType changes
  React.useEffect(() => {
    if (formData?.responsibleForActionType) {
      const currentType = formData.responsibleForActionType;
      
      // Clear fields that don't match the current type
      if (currentType !== 'USER' && formData.responsibleForActionUserIds) {
        setFormData(prev => ({ ...prev, responsibleForActionUserIds: [] }));
      }
      if (currentType !== 'ROLE' && formData.responsibleForActionRoleIds) {
        setFormData(prev => ({ ...prev, responsibleForActionRoleIds: [] }));
      }
      if (currentType !== 'DEPARTMENT' && formData.responsibleForActionDepartmentIds) {
        setFormData(prev => ({ ...prev, responsibleForActionDepartmentIds: [] }));
      }
    } else {
      // If type is cleared, clear all dependent fields
      if (formData?.responsibleForActionUserIds?.length || formData?.responsibleForActionRoleIds?.length || formData?.responsibleForActionDepartmentIds?.length) {
        setFormData(prev => ({
          ...prev,
          responsibleForActionUserIds: [],
          responsibleForActionRoleIds: [],
          responsibleForActionDepartmentIds: []
        }));
      }
    }
  }, [formData?.responsibleForActionType, setFormData]);

  // Auto-set QP User Signed At when QP User is selected (Product Status Change module)
  const prevQpUserIdRef = React.useRef(formData?.qpUserId);
  React.useEffect(() => {
    if (config?.entityName === 'ProductStatusChange') {
      const currentQpUserId = formData?.qpUserId;
      const prevQpUserId = prevQpUserIdRef.current;
      const currentQpUserSignedAt = formData?.qpUserSignedAt;
      
      // Check if qpUserId changed from empty to non-empty
      const wasEmpty = !prevQpUserId || prevQpUserId === '' || prevQpUserId === null;
      const isNotEmpty = currentQpUserId !== null && currentQpUserId !== undefined && currentQpUserId !== '';
      
      // Check if qpUserSignedAt is null or empty
      const isQpUserSignedAtNull = !currentQpUserSignedAt || 
                                   currentQpUserSignedAt === '' || 
                                   currentQpUserSignedAt === null || 
                                   currentQpUserSignedAt === undefined;
      
      // Only set timestamp if:
      // 1. qpUserId just became non-empty (changed from empty to non-empty)
      // 2. AND qpUserSignedAt is null/empty
      if (wasEmpty && isNotEmpty && isQpUserSignedAtNull) {
        // Format current datetime for datetime-local input (YYYY-MM-DDTHH:mm)
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
        
        setFormData(prev => ({ ...prev, qpUserSignedAt: datetimeLocal }));
      }
      
      // Update ref for next render
      prevQpUserIdRef.current = currentQpUserId;
    }
  }, [formData?.qpUserId, formData?.qpUserSignedAt, config?.entityName, setFormData]);

  const renderFormField = (field) => {
    if (!shouldShowFieldByDependsOn(field, formData, config)) {
      return null;
    }
    
    const rawValue = formData[field.name];
    const value =
      field.type === 'multiSelect'
        ? (Array.isArray(rawValue) ? rawValue : [])
        : (rawValue ?? '');
    
    // Check if field should be disabled based on formMode
    // Support both function and boolean for disabled property
    let isFieldDisabled = field.disabled === true;
    if (typeof field.disabled === 'function') {
      isFieldDisabled = field.disabled(formMode, formData);
    } else if (field.disabledInEdit && formMode === 'edit') {
      isFieldDisabled = true;
    }
    
    // Create enhanced field with disabled state
    const enhancedField = {
      ...field,
      disabled: isFieldDisabled
    };
    
    return renderField(enhancedField, {
      value,
      formData,
      setFormData,
      formErrors,
      setFormErrors,
      validateField,
      labels,
      config,
      setFilesToDelete,
      theme,
      confirmTaskDelete: () =>
        new Promise((resolve) => {
          taskDeleteResolverRef.current = resolve;
          setIsTaskDeleteConfirmOpen(true);
        })
    });
  };

  const renderFormSections = () => {
    // Check if we should use type-based sections (for complaints)
    let sectionsToUse = config.formSections;
    
    // If getFormSectionsByType exists and we have a complaintTypeId in formData, use dynamic sections
    if (config.getFormSectionsByType && typeof config.getFormSectionsByType === 'function') {
      const complaintTypeId = formData?.complaintTypeId;
      if (complaintTypeId) {
        // Get complaint types from lookup data
        const complaintTypes = config.lookupData?.complaintTypes?.data || 
                          config.lookupData?.complaintTypes || 
                          [];
        sectionsToUse = config.getFormSectionsByType(complaintTypeId, complaintTypes);
      }
    }
    
    // Determine which sections to use based on preset (if not using type-based)
    if (!sectionsToUse || sectionsToUse === config.formSections) {
      const defaultPreset = config.formLayoutPresets?.default || 'standard';
      const currentPreset = config.formLayoutPresets?.[defaultPreset];
      sectionsToUse = currentPreset?.useSections === false 
        ? currentPreset.formSections 
        : sectionsToUse;
    }
    
    if (!sectionsToUse) {
      // Fallback: render all fields vertically
      const fieldsToRemove = config.fieldsToRemove || [];
      return (
        <div className="space-y-6">
          {fields.filter(field => 
            field.type !== 'hidden' && 
            !fieldsToRemove.includes(field.name) &&
            !['createdAt', 'createdBy', 'updatedAt', 'updatedBy'].includes(field.name)
          ).map(renderFormField)}
        </div>
      );
    }

    return sectionsToUse.map((section, sectionIndex) => (
      <div key={sectionIndex} className="space-y-4">
        {/* Custom sections usually own their title; opt in with showSectionHeader */}
        {(section.layout !== 'custom' || section.showSectionHeader) && (
          <h3
            className={cn(
              'text-lg font-semibold border-b pb-2 flex items-center gap-2',
              theme.text.primary,
              theme.border.light,
            )}
          >
            {section.icon && <span>{section.icon}</span>}
            {section.title}
            {sectionIndex === 0 && (
              <button
                type="button"
                onClick={() => setIsHelpOpen(true)}
                className={cn(
                  'ml-auto p-2 rounded transition-colors hover:bg-slate-100',
                  theme.text.secondary,
                )}
                aria-label="Field descriptions"
                title="Field descriptions"
              >
                <QuestionCircleIcon className="w-4 h-4" />
              </button>
            )}
          </h3>
        )}
        <div className="space-y-6">
          {section.layout === 'grid' ? (
            // Grid layout for specific sections
            <div className={`grid grid-cols-1 ${section.gridCols || 'md:grid-cols-2'} gap-4`}>
              {section.fields.map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                if (!field || !shouldShowFieldByDependsOn(field, formData, config)) return null;
                
                return (
                  <div key={fieldName} className={section.fieldSpans?.[fieldName] || ''}>
                    {renderFormField(field)}
                  </div>
                );
              }).filter(Boolean)}
            </div>
          ) : section.layout === 'mixed' ? (
            // Mixed layout for deprecation section (switch full width, then grid)
            <div className="space-y-6">
              {/* Render switch field full width */}
              {(() => {
                const switchField = fields.find(f => f.name === 'deprecated');
                return switchField ? renderFormField(switchField) : null;
              })()}
              
              {/* Render other fields in grid */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {section.fields.filter(fieldName => fieldName !== 'deprecated').map(fieldName => {
                  const field = fields.find(f => f.name === fieldName);
                  if (!field || !shouldShowFieldByDependsOn(field, formData, config)) return null;
                  
                  const spanClass = fieldName === 'deprecatedAt' ? 'md:col-span-4' : 'md:col-span-8';
                  return (
                    <div key={fieldName} className={spanClass}>
                      {renderFormField(field)}
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>
          ) : section.layout === 'full' ? (
            // Full width layout (for file uploads, etc.)
            <div className="w-full">
              {section.fields.map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                if (!field || !shouldShowFieldByDependsOn(field, formData, config)) return null;
                
                return renderFormField(field);
              }).filter(Boolean)}
            </div>
          ) : section.layout === 'custom' ? (
            // Custom component layout
            section.customComponent ? (
              <section.customComponent
                complaintId={formData.id} // For complaint components
                deviationId={formData.id} // For deviation components
                formData={formData}
                setFormData={setFormData}
                config={config}
                fields={fields}
                isEditMode={formMode === 'edit'}
                selectedRecord={selectedRecord}
              />
            ) : null
          ) : (
            // Default vertical layout
            section.fields.map(fieldName => {
              const field = fields.find(f => f.name === fieldName);
              if (!field || !shouldShowFieldByDependsOn(field, formData, config)) return null;
              
              return renderFormField(field);
            }).filter(Boolean)
          )}
        </div>
      </div>
    ));
  };

  const renderOtherFields = () => {
    // Determine which sections to use based on preset
    const defaultPreset = config.formLayoutPresets?.default || 'standard';
    const currentPreset = config.formLayoutPresets?.[defaultPreset];
    const sectionsToUse = currentPreset?.useSections === false 
      ? currentPreset.formSections 
      : config.formSections;
    
    if (!sectionsToUse) return null;

    const sectionFields = sectionsToUse.flatMap(section => section.fields || []);
    // Also get fields from custom sections (they might be handled by custom components)
    const customSectionFields = sectionsToUse
      .filter(section => section.layout === 'custom' && section.fields)
      .flatMap(section => section.fields);
    
    // Get fields to remove from config
    const fieldsToRemove = config.fieldsToRemove || [];
    
    return fields.filter(field => 
      field.type !== 'hidden' && 
      !sectionFields.includes(field.name) &&
      !customSectionFields.includes(field.name) &&
      !fieldsToRemove.includes(field.name) &&
      !['createdAt', 'createdBy', 'updatedAt', 'updatedBy'].includes(field.name)
    ).map(renderFormField);
  };

  const renderTrackingSection = () => {
    const trackingConfig = config.trackingSection;
    if (!trackingConfig || !trackingConfig.enabled) return null;

    // Check if any tracking fields have data
    const hasTrackingData = trackingConfig.fields?.some((fieldConfig) => {
      const dateVal = formData?.[fieldConfig.dataField];
      const userVal = formData?.[fieldConfig.userField];
      const resolved = resolveTrackingUserValue(formData, fieldConfig, selectedRecord);
      return Boolean(
        dateVal ||
          userVal != null ||
          (resolved != null && typeof resolved === 'object'),
      );
    });

    if (!hasTrackingData) return null;

    return (
      <div className="space-y-4">
        <div
          className={cn(
            'rounded-lg border p-6 mt-8',
            theme.colors.surface,
            theme.border.light,
          )}
        >
          <h3 className={cn('text-lg font-semibold mb-6 flex items-center gap-2', theme.text.secondary)}>
            {trackingConfig.icon && <span>{trackingConfig.icon}</span>}
            {trackingConfig.title || `${entityName} Tracking Information`}
          </h3>
          
          <div className={`grid grid-cols-1 ${trackingConfig.layout === 'single' ? '' : 'md:grid-cols-2'} gap-6`}>
            {trackingConfig.fields?.map((fieldConfig, index) => {
              const dateValue = formData?.[fieldConfig.dataField];
              const userValue = resolveTrackingUserValue(formData, fieldConfig, selectedRecord);

              if (!dateValue && (userValue == null || userValue === '')) {
                return null;
              }

              return (
                <div
                  key={fieldConfig.id || index}
                  className={cn('rounded-lg border p-4', theme.colors.surface, theme.border.light)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-2 mb-4 font-semibold',
                      fieldConfig.color || theme.text.secondary,
                    )}
                  >
                    {fieldConfig.icon && <span>{fieldConfig.icon}</span>}
                    <span>{fieldConfig.label}</span>
                  </div>
                  
                  <div className="space-y-3">
                    {/* User Information */}
                    {fieldConfig.showUser !== false && (
                      <div className="flex items-start">
                        <span className={cn('font-medium min-w-12 mr-2', theme.text.secondary)}>
                          {fieldConfig.userLabel || 'By:'}
                        </span>
                        <span className={cn('font-medium', theme.text.primary)}>
                          {userValue != null && userValue !== '' ? (
                            typeof userValue === 'object' ? (
                              formatTrackingUserObject(userValue) || 'Unknown User'
                            ) : (
                              String(userValue)
                            )
                          ) : (
                            fieldConfig.userEmptyText || 'Not specified'
                          )}
                        </span>
                      </div>
                    )}

                    {/* Date Information */}
                    {fieldConfig.showDate !== false && (
                      <div className="flex items-start">
                        <span className={cn('font-medium min-w-12 mr-2', theme.text.secondary)}>
                          {fieldConfig.dateLabel || 'Date:'}
                        </span>
                        <span className={cn('font-medium', theme.text.primary)}>
                          {dateValue ? new Date(dateValue).toLocaleDateString(
                            trackingConfig.dateFormat?.locale || 'en-GB', 
                            trackingConfig.dateFormat?.options || {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }
                          ) : (fieldConfig.dateEmptyText || 'Not specified')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          {trackingConfig.showFooterNote !== false && (
            <div className={cn('mt-6 pt-4 border-t', theme.border.light)}>
              <p className={cn('text-xs italic flex items-center gap-2', theme.text.muted)}>
                {trackingConfig.footerIcon && <span>{trackingConfig.footerIcon}</span>}
                {trackingConfig.footerNote || 'This tracking information is automatically managed by the system and cannot be manually edited.'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <Drawer 
      isOpen={isOpen} 
      onClose={onClose}
      width={responsiveWidth}
    >
      {/* Header */}
      <DrawerHeader onClose={onClose}>
        <DrawerTitle>
          {formMode === 'add' ? labels.addTitle : labels.editTitle}
        </DrawerTitle>
      </DrawerHeader>

      {/* Inline Form Notifications */}
      {formNotifications && formNotifications.length > 0 && (
        <div className="px-6 py-2 space-y-2">
          {formNotifications.map(notification => (
            <div
              key={notification.id}
              className={`p-3 rounded-lg border animate-in slide-in-from-top-2 fade-in ${
                notification.type === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200' 
                  : notification.type === 'error' 
                  ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
                  : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
              }`}
            >
              {/* Header with icon, title, and close button */}
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-shrink-0">
                  {notification.type === 'success' && (
                    <CheckIcon className="w-5 h-5" />
                  )}
                  {notification.type === 'error' && (
                    <ExclamationTriangleIcon className="w-5 h-5" />
                  )}
                  {notification.type === 'info' && (
                    <InfoIcon className="w-5 h-5" />
                  )}
                </div>
                <div className="flex-1 text-sm font-semibold">
                  {notification.type === 'success' && 'Success'}
                  {notification.type === 'error' && 'Validation Failed'}
                  {notification.type === 'info' && 'Information'}
                </div>
                <button
                  onClick={() => setFormNotifications(prev => prev.filter(n => n.id !== notification.id))}
                  className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
              {/* Message content */}
              <div className="text-sm whitespace-pre-line pl-8">
                {notification.message}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Content */}
      <DrawerContent>
        <form onSubmit={onSubmit}>
          {/* Dynamic form sections based on config */}
          <div className="space-y-8">
            {renderFormSections()}

            {/* Other Fields not in sections */}
            {(() => {
              const otherFields = renderOtherFields();
              return otherFields && otherFields.length > 0 && (
                <div className="space-y-6">
                  {otherFields}
                </div>
              );
            })()}

            {/* Dynamic Tracking Information - Display Only */}
            {renderTrackingSection()}

            {/* Hidden Fields */}
            <div>
              {fields.filter(field => field.type === 'hidden').map(renderFormField)}
            </div>
          </div>
        </form>
      </DrawerContent>

      {/* Footer */}
      <DrawerFooter>
        <div className="flex space-x-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            {labels.cancelText}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isLoading}
            loading={isLoading}
            className="flex-1"
          >
            {formMode === 'add' ? labels.createText : labels.updateText} {entityName}
          </Button>
        </div>
        {!isFormValid && Object.keys(formErrors).length > 0 && (
          <div className="mt-2 text-sm text-red-600 text-center">
            Please fix the validation errors above before saving.
          </div>
        )}
      </DrawerFooter>
    </Drawer>
    {isHelpOpen && (
      <Modal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} size="sm" className="max-h-[45vh] overflow-hidden flex flex-col">
        <ModalHeader onClose={() => setIsHelpOpen(false)}>
          <ModalTitle>Field descriptions</ModalTitle>
        </ModalHeader>
        <ModalContent className="space-y-3 overflow-y-auto pr-2" maxHeight="[28vh]">
          {fields.map((f) => (
            <div key={f.name} className="border-b pb-2">
              <div className="text-sm font-semibold">{f.label || f.name}</div>
              <div className={`text-xs mt-1 ${theme.text.secondary}`}>
                {config?.helpText?.[f.name]?.short || 'No description available.'}
              </div>
            </div>
          ))}
        </ModalContent>
        <ModalFooter className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setIsHelpOpen(false)}
            className={cn(
              'px-4 py-2 rounded border hover:bg-slate-50',
              theme.border.light,
              theme.text.secondary,
            )}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    )}
    <ConfirmationDialog
      isOpen={isTaskDeleteConfirmOpen}
      onClose={() => {
        setIsTaskDeleteConfirmOpen(false);
        if (taskDeleteResolverRef.current) {
          taskDeleteResolverRef.current(false);
          taskDeleteResolverRef.current = null;
        }
      }}
      onConfirm={() => {
        setIsTaskDeleteConfirmOpen(false);
        if (taskDeleteResolverRef.current) {
          taskDeleteResolverRef.current(true);
          taskDeleteResolverRef.current = null;
        }
      }}
      title="Delete Task"
      message="Are you sure you want to delete this task?"
      confirmText="Delete"
      cancelText="Cancel"
      confirmVariant="destructive"
    />
    </>
  );
};

export default FormDrawer;
